import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import html2canvas from 'html2canvas';
import { CompanyInfo, LabelConfig, ServiceTicket } from '../types';
import { formatDateTR, formatDateShortTR } from './storage';

/**
 * Text helper that preserves full Turkish UTF-8 characters (ç, ğ, ı, ö, ş, ü, İ, etc.)
 */
export function toLabelAscii(text: string | undefined | null): string {
  if (!text) return '';
  return String(text);
}

export const PRESET_DIMENSIONS: Record<
  LabelConfig['preset'],
  { widthMm: number; heightMm: number; label: string; desc: string }
> = {
  '60x40': {
    widthMm: 60,
    heightMm: 40,
    label: '60 × 40 mm',
    desc: 'Standart Termal Fiş & Kasa Etiketi (En Popüler)',
  },
  '50x30': {
    widthMm: 50,
    heightMm: 30,
    label: '50 × 30 mm',
    desc: 'Kompakt Cihaz & Parça Etiketi',
  },
  '40x30': {
    widthMm: 40,
    heightMm: 30,
    label: '40 × 30 mm',
    desc: 'Mini Cihaz / Şarj / Kulaklık Etiketi',
  },
  '80x50': {
    widthMm: 80,
    heightMm: 50,
    label: '80 × 50 mm',
    desc: 'Geniş / Detaylı Servis Etiketi',
  },
  '100x50': {
    widthMm: 100,
    heightMm: 50,
    label: '100 × 50 mm',
    desc: 'Geniş Şerit / Koli Etiketi',
  },
  '100x70': {
    widthMm: 100,
    heightMm: 70,
    label: '100 × 70 mm',
    desc: 'Büyük Servis Kabul Formu Etiketi',
  },
  custom: {
    widthMm: 60,
    heightMm: 40,
    label: 'Özel Boyut',
    desc: 'İstediğiniz milimetrik boyutları girin',
  },
};

export const DEFAULT_LABEL_CONFIG: LabelConfig = {
  preset: '60x40',
  widthMm: 60,
  heightMm: 40,
  orientation: 'landscape',
  rotation: 0,

  showCompanyName: true,
  showCompanyPhone: true,
  showTicketId: true,
  showBarcode: false,
  barcodeType: 'none',
  barcodePosition: 'right',

  showCustomerName: true,
  showCustomerPhone: true,
  showDevicePassword: true,
  showIssueDescription: true,
  showAccessories: true,
  showReceivedDate: true, // Üst başlıkta saat olmadan tarih olarak görünür

  showDeviceType: false,
  showDeviceBrandModel: false,
  showSerialNumber: false,
  showWarrantyStatus: false,
  showCustomNote: true,
  customNoteText: 'Servis Takip Etiketi',

  headerAlignment: 'left',
  infoLayout: 'stacked',
  paddingMm: 2,
  borderStyle: 'solid',
  copies: 1,
};

const LABEL_STORAGE_KEY = 'service_custom_label_config_v2';

export function getStoredLabelConfig(): LabelConfig {
  try {
    const raw = localStorage.getItem(LABEL_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_LABEL_CONFIG };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_LABEL_CONFIG,
      ...parsed,
      showBarcode: false,
      barcodeType: 'none',
      infoLayout: 'stacked',
    };
  } catch {
    return { ...DEFAULT_LABEL_CONFIG };
  }
}

export function saveStoredLabelConfig(config: LabelConfig): void {
  try {
    localStorage.setItem(LABEL_STORAGE_KEY, JSON.stringify(config));

    // Send to PHP backend
    fetch('/api.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ labelConfig: config }),
    }).catch(() => {});

  } catch (err) {
    console.error('Failed to save label config:', err);
  }
}

export interface ActiveLabelField {
  label: string;
  val: string;
  isBadge?: boolean;
  fontSizePx: number;
  isContinuation?: boolean;
  indentPx?: number;
}

export interface LabelLayoutMetrics {
  effectiveWidth: number;
  effectiveHeight: number;
  previewWidthPx: number;
  previewHeightPx: number;
  previewPaddingPx: number;
  insideW: number;
  insideH: number;
  
  // Header
  hasHeader: boolean;
  headerTitleFontSize: number;
  headerPhoneFontSize: number;
  headerDateFontSize: number;
  headerDateText: string;
  ticketIdFontSize: number;
  headerHeight: number;
  
  // Footer
  hasFooter: boolean;
  footerFontSize: number;
  footerHeight: number;

  // Middle Information fields
  activeFields: ActiveLabelField[];
  infoLineHeight: number;
  infoFontSize: number;
  availableMiddleHeight: number;
  availableInfoWidth: number;
  
  // Codes
  hasBarcode: boolean;
  isSideBarcode: boolean;
  isBottomBarcode: boolean;
  sideWidth: number;
  qrSizePx: number;
  bcWidthPx: number;
  bcHeightPx: number;
  bcTextFontSize: number;
}

export const LABEL_FONT_SANS =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const LABEL_FONT_MONO =
  '"SF Mono", "Roboto Mono", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

let canvasMeasureContext: CanvasRenderingContext2D | null = null;

/**
 * Accurately measures text width in pixels using an internal offscreen 2D canvas.
 */
export function measureTextPx(text: string, font: string): number {
  if (!text) return 0;
  if (typeof document === 'undefined') return text.length * 7;
  if (!canvasMeasureContext) {
    const c = document.createElement('canvas');
    canvasMeasureContext = c.getContext('2d');
  }
  if (canvasMeasureContext) {
    canvasMeasureContext.font = font;
    return canvasMeasureContext.measureText(text).width;
  }
  return text.length * 7;
}

/**
 * Calculates dynamically scaled/shrunk font size so that a line fits within maxWidth.
 */
export function getFittingFontSize(
  text: string,
  maxWidth: number,
  baseFontSize: number,
  fontWeight: string = '900',
  fontFamily: string = LABEL_FONT_SANS,
  minFontSize: number = 6.5
): number {
  if (!text || maxWidth <= 0) return baseFontSize;
  const initialWidth = measureTextPx(text, `${fontWeight} ${baseFontSize}px ${fontFamily}`);
  if (initialWidth <= maxWidth) {
    return baseFontSize;
  }
  // Proportionally scale down font size
  const ratio = maxWidth / initialWidth;
  const calculated = baseFontSize * ratio * 0.98; // 2% safety margin for maximum readable size
  return Math.max(minFontSize, Math.round(calculated * 10) / 10);
}

/**
 * Truncates text with ellipsis if it exceeds maxWidth even after scaling
 */
export function truncateTextToFit(
  text: string,
  maxWidth: number,
  font: string
): string {
  if (!text || maxWidth <= 0) return text;
  if (measureTextPx(text, font) <= maxWidth) return text;

  let truncated = text;
  while (truncated.length > 1 && measureTextPx(truncated + '...', font) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated.length > 0 ? truncated + '...' : text;
}

/**
 * Wraps raw label fields into multiple lines so that long texts (e.g. fault descriptions, accessories, customer names)
 * seamlessly flow into the next line instead of being cut off.
 */
export function buildWrappedLabelFields(
  rawFields: Array<{ label: string; val: string; isBadge?: boolean }>,
  availableWidth: number,
  targetFontSize: number,
  generalScale: number
): ActiveLabelField[] {
  const result: ActiveLabelField[] = [];

  for (const f of rawFields) {
    if (!f.val) continue;

    if (f.isBadge) {
      // Badges stay on 1 line
      const lblFont = `800 ${Math.round(targetFontSize * 0.92)}px ${LABEL_FONT_SANS}`;
      const lblW = measureTextPx(f.label + ' ', lblFont);
      const availForVal = Math.max(20, availableWidth - lblW - Math.round(4 * generalScale));
      const fittedSize = getFittingFontSize(
        f.val,
        availForVal,
        targetFontSize,
        '800',
        LABEL_FONT_MONO,
        Math.max(6, Math.round(7 * generalScale))
      );
      result.push({
        label: f.label,
        val: f.val,
        isBadge: true,
        fontSizePx: fittedSize,
        isContinuation: false,
        indentPx: 0,
      });
      continue;
    }

    const lblFont = `800 ${Math.round(targetFontSize * 0.92)}px ${LABEL_FONT_SANS}`;
    const valFont = `700 ${targetFontSize}px ${LABEL_FONT_SANS}`;
    const lblW = measureTextPx(f.label + ' ', lblFont);

    const firstLineValMaxW = Math.max(20, availableWidth - lblW - Math.round(2 * generalScale));
    const fullValWidth = measureTextPx(f.val, valFont);

    // If whole text fits in the first line:
    if (fullValWidth <= firstLineValMaxW) {
      result.push({
        label: f.label,
        val: f.val,
        isBadge: false,
        fontSizePx: targetFontSize,
        isContinuation: false,
        indentPx: 0,
      });
      continue;
    }

    // Split into words and wrap to next lines
    const words = f.val.split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;

    // Indent continuation lines under the label's value position
    const continuationIndent = Math.min(
      Math.round(lblW),
      Math.max(Math.round(8 * generalScale), Math.round(availableWidth * 0.18))
    );
    const continuationMaxW = Math.max(20, availableWidth - continuationIndent - Math.round(2 * generalScale));

    let currentLine = '';
    let isFirstLine = true;

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const maxW = isFirstLine ? firstLineValMaxW : continuationMaxW;
      const testLine = currentLine ? `${currentLine} ${word}` : word;

      if (measureTextPx(testLine, valFont) <= maxW) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          result.push({
            label: isFirstLine ? f.label : '',
            val: currentLine,
            isBadge: false,
            fontSizePx: targetFontSize,
            isContinuation: !isFirstLine,
            indentPx: isFirstLine ? 0 : continuationIndent,
          });
          isFirstLine = false;
          currentLine = word;
        } else {
          // If a single word is wider than maxW, push as-is
          result.push({
            label: isFirstLine ? f.label : '',
            val: word,
            isBadge: false,
            fontSizePx: targetFontSize,
            isContinuation: !isFirstLine,
            indentPx: isFirstLine ? 0 : continuationIndent,
          });
          isFirstLine = false;
          currentLine = '';
        }
      }
    }

    if (currentLine) {
      result.push({
        label: isFirstLine ? f.label : '',
        val: currentLine,
        isBadge: false,
        fontSizePx: targetFontSize,
        isContinuation: !isFirstLine,
        indentPx: isFirstLine ? 0 : continuationIndent,
      });
    }
  }

  return result;
}

/**
 * High-Resolution Dots Per Millimeter (300 DPI Thermal standard: 300 / 25.4 = 11.81 => 12 DPM)
 */
export const LABEL_DPM = 12;

/**
 * Pure auto-fit layout engine: Calculates optimal sizes, spacing, and per-line font reduction
 * to perfectly fit all enabled content inside the given label dimensions.
 */
export function calculateLabelMetrics(
  ticket: ServiceTicket,
  companyInfo: CompanyInfo,
  config: LabelConfig
): LabelLayoutMetrics {
  const effectiveWidth = Number(config.widthMm) || 60;
  const effectiveHeight = Number(config.heightMm) || 40;

  // Logical coordinate system scaled to physical dimensions
  const previewWidthPx = Math.round(effectiveWidth * LABEL_DPM);
  const previewHeightPx = Math.round(effectiveHeight * LABEL_DPM);
  
  // Padding proportional to millimeter setting (safe bounds 1mm to 3mm)
  const padMm = Math.max(1, Math.min(3, config.paddingMm ?? 2));
  const previewPaddingPx = Math.round(padMm * LABEL_DPM);

  const insideW = Math.max(80, previewWidthPx - previewPaddingPx * 2);
  const insideH = Math.max(60, previewHeightPx - previewPaddingPx * 2);

  // Overall scale factor relative to standard 60x40 mm
  const heightScale = Math.max(0.5, Math.min(2.5, effectiveHeight / 40));
  const widthScale = Math.max(0.5, Math.min(2.5, effectiveWidth / 60));
  const generalScale = Math.min(widthScale, heightScale);

  // Header calculations
  const headerDateText = (config.showReceivedDate && ticket.createdAt) ? formatDateShortTR(ticket.createdAt) : '';
  const hasHeader = !!(config.showCompanyName || config.showTicketId || headerDateText || (config.showCompanyPhone && companyInfo.phone));

  // Header Height budget
  const maxHeaderH = hasHeader ? Math.min(insideH * 0.26, Math.max(insideH * 0.16, 6.5 * LABEL_DPM)) : 0;

  let headerTitleFontSize = Math.min(Math.round(25 * generalScale), Math.round(maxHeaderH * 0.46));
  const headerPhoneFontSize = Math.min(Math.round(15 * generalScale), Math.round(maxHeaderH * 0.28));
  const headerDateFontSize = Math.min(Math.round(14 * generalScale), Math.round(maxHeaderH * 0.26));
  const ticketIdFontSize = Math.min(Math.round(25 * generalScale), Math.round(maxHeaderH * 0.46));

  // Calculate ticket badge width so title never overlaps badge
  const ticketBadgeWidth = config.showTicketId
    ? measureTextPx(ticket.id, `900 ${ticketIdFontSize}px ${LABEL_FONT_MONO}`) + Math.round(14 * generalScale)
    : 0;

  if (hasHeader && config.showCompanyName && companyInfo.name) {
    const availableHeaderTitleW = Math.max(30, insideW - ticketBadgeWidth - Math.round(8 * generalScale));
    headerTitleFontSize = getFittingFontSize(
      companyInfo.name.toUpperCase(),
      availableHeaderTitleW,
      headerTitleFontSize,
      '900',
      LABEL_FONT_SANS,
      Math.round(12 * generalScale)
    );
  }
  
  let headerHeight = 0;
  if (hasHeader) {
    const textStackH =
      (config.showCompanyName ? headerTitleFontSize : 0) +
      (config.showCompanyPhone && companyInfo.phone ? headerPhoneFontSize + Math.round(3 * generalScale) : 0);
    const badgeH =
      (config.showTicketId ? ticketIdFontSize + Math.round(5 * generalScale) : 0) +
      (headerDateText ? headerDateFontSize + Math.round(2 * generalScale) : 0);
    headerHeight = Math.min(maxHeaderH, Math.max(textStackH, badgeH) + Math.round(4 * generalScale));
  }

  // Footer calculations
  const hasFooter = !!(config.showCustomNote && config.customNoteText);
  let footerFontSize = Math.max(Math.round(11 * generalScale), Math.min(Math.round(15 * generalScale), Math.round(insideH * 0.05)));
  if (hasFooter && config.customNoteText) {
    footerFontSize = getFittingFontSize(
      config.customNoteText,
      insideW,
      footerFontSize,
      '800',
      LABEL_FONT_SANS,
      Math.round(9 * generalScale)
    );
  }
  const footerHeight = hasFooter ? footerFontSize + Math.round(6 * generalScale) : 0;

  // Middle available height budget (All space is dedicated to pure text!)
  const availableMiddleHeight = Math.max(
    30,
    insideH - headerHeight - footerHeight - (hasHeader ? Math.round(6 * generalScale) : 0) - (hasFooter ? Math.round(4 * generalScale) : 0)
  );

  // Raw fields list (Middle content: Customer Name, Phone, Brand, Password, Accessories, Serial, Warranty, Issue Description at the bottom)
  const rawFields: Array<{ label: string; val: string; isBadge?: boolean }> = [];
  if (config.showCustomerName && ticket.customerName) {
    rawFields.push({ label: 'MÜŞTERİ:', val: ticket.customerName });
  }
  if (config.showCustomerPhone && ticket.customerPhone) {
    rawFields.push({ label: 'TEL:', val: ticket.customerPhone });
  }
  if (config.showDeviceBrandModel && (ticket.deviceBrand || ticket.deviceModel)) {
    rawFields.push({
      label: 'CİHAZ:',
      val: `${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim(),
    });
  }
  if (config.showDevicePassword && ticket.devicePassword) {
    rawFields.push({ label: 'ŞİFRE:', val: ticket.devicePassword, isBadge: true });
  }

  // Included Accessories
  const accessoriesList = Array.isArray(ticket.includedAccessories)
    ? ticket.includedAccessories.filter(Boolean)
    : typeof ticket.includedAccessories === 'string'
      ? [ticket.includedAccessories]
      : [];
  const accessoriesText = accessoriesList.join(', ');

  if ((config.showAccessories ?? true) && accessoriesText) {
    rawFields.push({ label: 'AKSESUAR:', val: accessoriesText });
  }

  // Optional extra fields (only if enabled by custom settings)
  if (config.showSerialNumber && ticket.serialNumber) {
    rawFields.push({ label: 'SERİ NO:', val: ticket.serialNumber });
  }
  if (config.showWarrantyStatus) {
    rawFields.push({
      label: 'GARANTİ:',
      val: ticket.isUnderWarranty ? 'Garantili' : 'Garantisiz',
    });
  }

  // Arıza / Şikayet Açıklaması en altta yer alır (uzun metinler alt satıra geçerek düzeni bozmaz)
  if (config.showIssueDescription && ticket.issueDescription) {
    rawFields.push({ label: 'ARIZA:', val: ticket.issueDescription });
  }

  // Available horizontal width for info lines (100% of inner width)
  const availableInfoWidth = insideW;

  // 1. Initial generous font size based on height & estimated fields count
  const estimatedFields = Math.max(1, rawFields.length);
  const idealFontByHeight = Math.round((availableMiddleHeight / (estimatedFields + 0.3)) * 0.52);
  let targetFontSize = Math.max(
    Math.round(13 * generalScale),
    Math.min(Math.round(23 * generalScale), idealFontByHeight)
  );

  // 2. Build wrapped lines (wrapping long lines onto next lines)
  let activeFields = buildWrappedLabelFields(rawFields, availableInfoWidth, targetFontSize, generalScale);

  // 3. Calculate line step and adjust font size if there are multiple wrapped lines
  let totalLineCount = Math.max(1, activeFields.length);
  let lineStep = availableMiddleHeight / totalLineCount;

  if (lineStep < targetFontSize * 1.25) {
    targetFontSize = Math.max(
      Math.round(11 * generalScale),
      Math.min(targetFontSize, Math.round(lineStep * 0.65))
    );
    // Re-wrap with final calibrated font size
    activeFields = buildWrappedLabelFields(rawFields, availableInfoWidth, targetFontSize, generalScale);
    totalLineCount = Math.max(1, activeFields.length);
    lineStep = Math.max(Math.round(14 * generalScale), availableMiddleHeight / totalLineCount);
  } else if (lineStep > 46 * generalScale) {
    lineStep = Math.round(46 * generalScale);
  }

  const infoLineHeight = lineStep;
  const infoFontSize = targetFontSize;

  return {
    effectiveWidth,
    effectiveHeight,
    previewWidthPx,
    previewHeightPx,
    previewPaddingPx,
    insideW,
    insideH,
    hasHeader,
    headerTitleFontSize,
    headerPhoneFontSize,
    headerDateFontSize,
    headerDateText,
    ticketIdFontSize,
    headerHeight,
    hasFooter,
    footerFontSize,
    footerHeight,
    activeFields,
    infoLineHeight,
    infoFontSize,
    availableMiddleHeight,
    availableInfoWidth,
    hasBarcode: false,
    isSideBarcode: false,
    isBottomBarcode: false,
    sideWidth: 0,
    qrSizePx: 0,
    bcWidthPx: 0,
    bcHeightPx: 0,
    bcTextFontSize: 0,
  };
}

/**
 * Generate QR code data URL with high contrast and density
 */
export async function generateQRCodeDataUrl(text: string): Promise<string> {
  try {
    return await QRCode.toDataURL(text || 'SERVIS', {
      margin: 1,
      width: 480,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    });
  } catch (err) {
    console.error('QR code generation error:', err);
    return '';
  }
}

/**
 * Generate 1D Code128 Barcode data URL with clear bars and essential quiet zones for scanners
 */
export function generateBarcodeDataUrl(text: string): string {
  try {
    const canvas = document.createElement('canvas');
    const cleanText = (text || '000000').trim().toUpperCase();
    JsBarcode(canvas, cleanText, {
      format: 'CODE128',
      displayValue: false,
      margin: 12, // High quiet zone ensures optical & laser CCD barcode scanners read instantly
      height: 90,
      width: 3.4,
      background: '#ffffff',
      lineColor: '#000000',
      valid: () => true,
    });
    return canvas.toDataURL('image/png');
  } catch (err) {
    console.error('Barcode generation error:', err);
    return '';
  }
}

/**
 * Helper to load an image from a data URL into an HTMLImageElement
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = e => reject(e);
    img.src = src;
  });
}

/**
 * High-precision Direct Canvas 2D Renderer for label with 100% Barcode & QR support.
 * Uses 12 DPM (~305 DPI) native supersampling and pure monochrome contrast for razor-sharp thermal output.
 */
export async function renderLabelToCanvas(
  ticket: ServiceTicket,
  companyInfo: CompanyInfo,
  config: LabelConfig,
  applyRotation: boolean = true
): Promise<HTMLCanvasElement> {
  const m = calculateLabelMetrics(ticket, companyInfo, config);

  const canvasW = m.previewWidthPx;
  const canvasH = m.previewHeightPx;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return canvas;

  // Clear to pure white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasW, canvasH);

  const pad = m.previewPaddingPx;
  const innerW = m.insideW;
  const innerH = m.insideH;

  // Overall scale factor relative to standard 60x40 mm
  const heightScale = Math.max(0.45, Math.min(2.5, m.effectiveHeight / 40));
  const widthScale = Math.max(0.45, Math.min(2.5, m.effectiveWidth / 60));
  const generalScale = Math.min(widthScale, heightScale);

  let curY = pad;

  // 1. Header: Company Name & Ticket ID Badge & Date
  if (m.hasHeader) {
    const headerFontSize = m.headerTitleFontSize;
    ctx.fillStyle = '#000000';
    ctx.font = `900 ${headerFontSize}px ${LABEL_FONT_SANS}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const companyTitle = String(companyInfo.name || '').toUpperCase().slice(0, 36);
    if (config.showCompanyName) {
      ctx.fillText(companyTitle, pad, curY);
    }

    let rightColumnH = 0;
    let rightY = curY;

    if (config.showTicketId) {
      const ticketFontSize = m.ticketIdFontSize;
      ctx.font = `900 ${ticketFontSize}px ${LABEL_FONT_MONO}`;
      const ticketText = ticket.id;
      const textWidth = ctx.measureText(ticketText).width;
      const badgePadX = Math.round(5 * generalScale);
      const badgePadY = Math.round(2 * generalScale);
      const badgeW = textWidth + badgePadX * 2;
      const badgeH = ticketFontSize + badgePadY * 2;

      // Solid black badge background for maximum contrast
      ctx.fillStyle = '#000000';
      ctx.fillRect(pad + innerW - badgeW, rightY, badgeW, badgeH);

      // Crisp white badge text
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.fillText(ticketText, pad + innerW - badgeW / 2, rightY + badgePadY);

      rightY += badgeH + Math.round(2 * generalScale);
      rightColumnH += badgeH + Math.round(2 * generalScale);
    }

    if (m.headerDateText) {
      const dateFontSize = m.headerDateFontSize;
      ctx.font = `700 ${dateFontSize}px ${LABEL_FONT_MONO}`;
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'right';
      ctx.fillText(m.headerDateText, pad + innerW, rightY);
      rightColumnH += dateFontSize + Math.round(2 * generalScale);
    }

    let leftColumnH = config.showCompanyName ? headerFontSize + Math.round(2 * generalScale) : 0;

    if (config.showCompanyPhone && companyInfo.phone) {
      const phoneFontSize = m.headerPhoneFontSize;
      ctx.font = `700 ${phoneFontSize}px ${LABEL_FONT_SANS}`;
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'left';
      ctx.fillText(`Tel: ${companyInfo.phone}`, pad, curY + (config.showCompanyName ? headerFontSize + Math.round(2 * generalScale) : 0));
      leftColumnH += phoneFontSize + Math.round(3 * generalScale);
    }

    curY += Math.max(leftColumnH, rightColumnH) + Math.round(4 * generalScale);

    // Header solid separator line
    ctx.lineWidth = Math.max(1.5, Math.round(1.5 * generalScale));
    ctx.strokeStyle = '#000000';
    ctx.beginPath();
    ctx.moveTo(pad, curY);
    ctx.lineTo(pad + innerW, curY);
    ctx.stroke();
    curY += Math.max(4, Math.round(5 * generalScale));
  }

  // 2. Middle Information fields (Full-width, large clear fonts, automatic wrapping)
  const infoX = pad;
  const infoStartY = curY + Math.max(1, Math.round(2 * generalScale));
  const infoW = m.availableInfoWidth;

  const lineStep = m.infoLineHeight;

  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  for (let i = 0; i < m.activeFields.length; i++) {
    const item = m.activeFields[i];
    const rowFontSize = item.fontSizePx;
    const textY = infoStartY + i * lineStep + Math.max(0, (lineStep - rowFontSize) / 2);

    if (item.label) {
      // Primary line with label (e.g. "MÜŞTERİ:", "TEL:", "ŞİFRE:", "ARIZA:")
      const lblFont = `900 ${Math.round(rowFontSize * 0.95)}px ${LABEL_FONT_SANS}`;
      ctx.font = lblFont;
      ctx.fillStyle = '#000000';
      const lbl = item.label + ' ';
      ctx.fillText(lbl, infoX, textY);
      const lblWidth = ctx.measureText(lbl).width;

      const valAvailableW = Math.max(20, infoW - lblWidth - Math.round(4 * generalScale));
      const valFont = `${item.isBadge ? '900' : '700'} ${rowFontSize}px ${item.isBadge ? LABEL_FONT_MONO : LABEL_FONT_SANS}`;
      const truncatedVal = truncateTextToFit(item.val, valAvailableW, valFont);

      // Draw value in solid black
      if (item.isBadge) {
        // Clean boxed password badge with solid border
        ctx.font = valFont;
        const valW = ctx.measureText(truncatedVal).width;
        const bPadX = Math.round(5 * generalScale);
        const bPadY = Math.round(1.5 * generalScale);
        
        ctx.lineWidth = Math.max(1.5, Math.round(1.5 * generalScale));
        ctx.strokeStyle = '#000000';
        ctx.strokeRect(
          infoX + lblWidth - bPadX,
          textY - bPadY,
          valW + bPadX * 2,
          rowFontSize + bPadY * 2 + Math.round(2 * generalScale)
        );
        ctx.fillStyle = '#000000';
        ctx.fillText(truncatedVal, infoX + lblWidth, textY);
      } else {
        ctx.font = valFont;
        ctx.fillStyle = '#000000';
        ctx.fillText(truncatedVal, infoX + lblWidth, textY);
      }
    } else {
      // Wrapped continuation line
      const indent = item.indentPx || 0;
      const lineX = infoX + indent;
      const valAvailableW = Math.max(20, infoW - indent - Math.round(4 * generalScale));
      const valFont = `700 ${rowFontSize}px ${LABEL_FONT_SANS}`;
      const truncatedVal = truncateTextToFit(item.val, valAvailableW, valFont);

      ctx.font = valFont;
      ctx.fillStyle = '#000000';
      ctx.fillText(truncatedVal, lineX, textY);
    }
  }

  // 3. Custom Footer Note
  if (m.hasFooter) {
    const footerFontSize = m.footerFontSize;
    ctx.font = `800 ${footerFontSize}px ${LABEL_FONT_SANS}`;
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    // Divider line above footer
    ctx.lineWidth = Math.max(1.5, Math.round(1.5 * generalScale));
    ctx.strokeStyle = '#000000';
    ctx.beginPath();
    ctx.moveTo(pad, canvasH - pad - footerFontSize - Math.round(5 * generalScale));
    ctx.lineTo(pad + innerW, canvasH - pad - footerFontSize - Math.round(5 * generalScale));
    ctx.stroke();

    const truncatedFooter = truncateTextToFit(config.customNoteText, innerW, `800 ${footerFontSize}px ${LABEL_FONT_SANS}`);
    ctx.fillText(truncatedFooter, pad + innerW / 2, canvasH - pad);
  }

  // 4. Apply rotation if requested (for print/PDF matching thermal roll direction)
  if (applyRotation && config.rotation) {
    const rot = config.rotation;
    const isRot90or270 = rot === 90 || rot === 270;
    const rotCanvas = document.createElement('canvas');
    rotCanvas.width = isRot90or270 ? canvasH : canvasW;
    rotCanvas.height = isRot90or270 ? canvasW : canvasH;
    const rctx = rotCanvas.getContext('2d', { alpha: false });
    if (rctx) {
      rctx.fillStyle = '#ffffff';
      rctx.fillRect(0, 0, rotCanvas.width, rotCanvas.height);
      rctx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
      rctx.rotate((rot * Math.PI) / 180);
      rctx.drawImage(canvas, -canvasW / 2, -canvasH / 2);
      return rotCanvas;
    }
  }

  return canvas;
}

/**
 * Capture preview with direct high-precision canvas renderer guaranteeing 100% Barcode & QR fidelity
 */
export async function captureLabelPreviewImage(
  ticket: ServiceTicket,
  companyInfo: CompanyInfo,
  config: LabelConfig,
  previewElement?: HTMLElement | HTMLCanvasElement | null,
  applyRotation: boolean = true
): Promise<string> {
  if (previewElement instanceof HTMLCanvasElement && previewElement.width > 50 && !config.rotation) {
    return previewElement.toDataURL('image/png', 1.0);
  }
  const canvas = await renderLabelToCanvas(ticket, companyInfo, config, applyRotation);
  return canvas.toDataURL('image/png', 1.0);
}

/**
 * Builds jsPDF document for label with exact millimeter dimensions
 */
export async function buildCustomLabelDoc(
  ticket: ServiceTicket,
  companyInfo: CompanyInfo,
  config: LabelConfig,
  previewElement?: HTMLElement | null
): Promise<jsPDF> {
  const rot = config.rotation || 0;
  const isRot90or270 = rot === 90 || rot === 270;

  const baseWidth = Number(config.widthMm) || 60;
  const baseHeight = Number(config.heightMm) || 40;

  const printWidth = isRot90or270 ? baseHeight : baseWidth;
  const printHeight = isRot90or270 ? baseWidth : baseHeight;

  const imgData = await captureLabelPreviewImage(
    ticket,
    companyInfo,
    config,
    previewElement,
    true
  );

  const isPortrait = printHeight >= printWidth;
  const doc = new jsPDF({
    orientation: isPortrait ? 'p' : 'l',
    unit: 'mm',
    format: [printWidth, printHeight],
    compress: true,
  });

  const copies = Math.max(1, config.copies || 1);
  for (let i = 0; i < copies; i++) {
    if (i > 0) {
      doc.addPage([printWidth, printHeight], isPortrait ? 'p' : 'l');
    }
    doc.addImage(imgData, 'PNG', 0, 0, printWidth, printHeight, undefined, 'FAST');
  }

  return doc;
}

/**
 * Export label as PDF file
 */
export async function exportCustomLabelPDF(
  ticket: ServiceTicket,
  companyInfo: CompanyInfo,
  config: LabelConfig,
  previewElement?: HTMLElement | null
): Promise<void> {
  try {
    const doc = await buildCustomLabelDoc(ticket, companyInfo, config, previewElement);
    const fileName = `Etiket_${ticket.id}.pdf`;

    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      try {
        if (document.body.contains(a)) document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {}
    }, 1500);
  } catch (err) {
    console.error('PDF export error:', err);
  }
}

/**
 * Direct print label to thermal printer with exact millimeter page sizing.
 * Strips title and forces zero margin to completely eliminate browser headers/footers.
 */
export async function printCustomLabel(
  ticket: ServiceTicket,
  companyInfo: CompanyInfo,
  config: LabelConfig,
  previewElement?: HTMLElement | null
): Promise<void> {
  try {
    const rot = config.rotation || 0;
    const isRot90or270 = rot === 90 || rot === 270;

    const baseWidth = Number(config.widthMm) || 60;
    const baseHeight = Number(config.heightMm) || 40;

    const effectiveWidth = isRot90or270 ? baseHeight : baseWidth;
    const effectiveHeight = isRot90or270 ? baseWidth : baseHeight;

    const imgUrl = await captureLabelPreviewImage(
      ticket,
      companyInfo,
      config,
      previewElement,
      true
    );

    const copies = Math.max(1, config.copies || 1);

    // Multi-copy pages markup
    let pagesHtml = '';
    for (let i = 0; i < copies; i++) {
      pagesHtml += `
        <div class="label-page">
          <img src="${imgUrl}" alt="" />
        </div>
      `;
    }

    // Notice: Title is deliberately left completely blank to avoid browser printing header text
    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title></title>
          <style>
            @page {
              size: ${effectiveWidth}mm ${effectiveHeight}mm;
              margin: 0 !important;
              padding: 0 !important;
            }
            @media print {
              @page {
                size: ${effectiveWidth}mm ${effectiveHeight}mm;
                margin: 0 !important;
              }
              *, *::before, *::after {
                box-sizing: border-box !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              html, body {
                width: ${effectiveWidth}mm !important;
                height: ${effectiveHeight}mm !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: hidden !important;
                background-color: #ffffff !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .label-page {
                width: ${effectiveWidth}mm !important;
                height: ${effectiveHeight}mm !important;
                page-break-after: always !important;
                page-break-inside: avoid !important;
                break-after: page !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                overflow: hidden !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .label-page:last-child {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              img {
                width: ${effectiveWidth}mm !important;
                height: ${effectiveHeight}mm !important;
                object-fit: fill !important;
                display: block !important;
                margin: 0 !important;
                padding: 0 !important;
              }
            }
            *, *::before, *::after {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            html, body {
              width: ${effectiveWidth}mm;
              height: ${effectiveHeight}mm;
              margin: 0;
              padding: 0;
              background-color: #ffffff;
            }
            .label-page {
              width: ${effectiveWidth}mm;
              height: ${effectiveHeight}mm;
              page-break-after: always;
              page-break-inside: avoid;
              break-after: page;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
            }
            .label-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }
            img {
              width: ${effectiveWidth}mm;
              height: ${effectiveHeight}mm;
              object-fit: fill;
              display: block;
            }
          </style>
        </head>
        <body>
          ${pagesHtml}
          <script>
            window.onload = function() {
              window.focus();
              setTimeout(function() {
                window.print();
              }, 120);
            };
          </script>
        </body>
      </html>
    `;

    // Use hidden iframe for seamless direct thermal printing
    const iframeId = 'thermal-label-print-frame';
    let iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }

    iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document || iframe.contentDocument;
    if (doc) {
      doc.open();
      doc.write(printHtml);
      doc.close();
    } else {
      // Popup fallback
      const printWin = window.open('', '_blank', 'width=450,height=450');
      if (printWin) {
        printWin.document.open();
        printWin.document.write(printHtml);
        printWin.document.close();
      }
    }
  } catch (err) {
    console.error('Print label error:', err);
  }
}

/**
 * Direct thermal printing via vector-perfect PDF blob stream.
 * PDF printing completely avoids all browser HTML header/footer injections (Date, URL, Title).
 */
export async function printCustomLabelPDF(
  ticket: ServiceTicket,
  companyInfo: CompanyInfo,
  config: LabelConfig,
  previewElement?: HTMLElement | null
): Promise<void> {
  try {
    const doc = await buildCustomLabelDoc(ticket, companyInfo, config, previewElement);
    doc.autoPrint();
    const blob = doc.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    const iframeId = 'thermal-label-pdf-frame';
    let iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }

    iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      try {
        iframe?.contentWindow?.focus();
        iframe?.contentWindow?.print();
      } catch {
        const win = window.open(blobUrl, '_blank');
        if (win) win.focus();
      }
    };

    iframe.onload = () => {
      setTimeout(triggerPrint, 300);
    };

    setTimeout(triggerPrint, 1200);

    setTimeout(() => {
      try {
        if (document.body.contains(iframe!)) {
          document.body.removeChild(iframe!);
        }
      } catch {}
      URL.revokeObjectURL(blobUrl);
    }, 60000);
  } catch (err) {
    console.error('PDF label print error, falling back to direct:', err);
    await printCustomLabel(ticket, companyInfo, config, previewElement);
  }
}

